import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { localeAwareFormatDurationShort } from 'app/lib/date';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { getListingTransactionMode } from 'app/lib/listing';
import type { RecentLoanListing } from 'app/network/loan';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import { createTheme as createDSTheme } from 'app/shopify';
import { TransactionMode, type Listing } from 'app/types/listing';
import type { RecommendedListing } from 'app/types/recommender';
import { useUISize } from 'app/utils/web-helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { ListingThumbnail } from './listing-thumbnail';
const theme = createDSTheme();
const CARD_WIDTH_SMALL = 140;
const CARD_WIDTH_LARGE = 180;
const AVATAR_SIZE = 40;
const AVATAR_BORDER = 2;

type RecentLoanCardVariant = 'sharing' | 'exchange';

const exchangeBadgeBackgroundColorTokens = {
  [TransactionMode.lend]: theme.colors['surface.brand.secondary-5.default'],
  [TransactionMode.sell]: theme.colors['surface.brand.secondary-2.default'],
  [TransactionMode.give]: theme.colors['surface.brand.secondary-4.default'],
} as const satisfies Record<TransactionMode, string>;

function RecentExchangeBadge({ mode }: { mode: TransactionMode }) {
  const { t } = useTranslation('recommendations');

  return (
    <View
      style={{
        position: 'absolute',
        left: 8,
        top: 8,
        zIndex: 1,
        borderRadius: 1000,
        backgroundColor: exchangeBadgeBackgroundColorTokens[mode],
        paddingHorizontal: 8,
        paddingVertical: 4,
      }}
    >
      <Text color="text.primary" fontSize={14} fontWeight="700">
        {t(`recent-loans.badge.${mode}`, { defaultValue: mode })}
      </Text>
    </View>
  );
}

function isRecentLoanListing(listing: RecentLoanListing | Listing | RecommendedListing): listing is RecentLoanListing {
  return 'borrower' in listing && 'lender' in listing && 'completedAt' in listing;
}

export function RecentLoanCard({
  listing,
  variant = 'sharing',
}: {
  listing: RecentLoanListing | Listing | RecommendedListing;
  variant?: RecentLoanCardVariant;
}) {
  const { t } = useTranslation('recommendations');
  const { isSmallUI } = useUISize();
  const navigation = useNavigation();

  if (!isRecentLoanListing(listing)) {
    return null;
  }

  const { borrower, lender, completedAt, type, subtype, before } = listing;
  const thumbPicture = listing.picture ?? listing.pictures?.[0]?.url;

  const borrowerName = borrower.profile?.firstName ?? '';
  const lenderName = lender.profile?.firstName ?? '';
  const itemName = listing.item.name;

  const timeAgo = completedAt ? localeAwareFormatDurationShort(new Date(completedAt)) : '';
  const CARD_WIDTH = isSmallUI ? CARD_WIDTH_SMALL : CARD_WIDTH_LARGE;
  const exchangeMode = getListingTransactionMode(listing);
  return (
    <Column style={{ width: CARD_WIDTH }} gap="spacing.general.x2" marginVertical="spacing.general.x2">
      {/* Thumbnail with overlapping avatars */}
      <Pressable onPress={() => navigateToListing(navigation, listing.id)}>
        <ListingThumbnail
          picture={thumbPicture}
          type={type}
          subtype={subtype}
          transactionMode={listing.transactionMode}
          isDonation={listing.isDonation}
          before={before}
          width="100%"
          isFound={!!lender}
          skipDonationOverlay={variant === 'exchange'}
        />

        {variant === 'exchange' ? <RecentExchangeBadge mode={exchangeMode} /> : null}

        {/* Overlapping avatars at the bottom center */}
        <Row
          alignItems="center"
          justifyContent="center"
          style={{
            position: 'absolute',
            bottom: -(AVATAR_SIZE / 2),
            left: 0,
            right: 0,
          }}
        >
          {lender.profile?.picture ? (
            <View
              style={{
                zIndex: 1,
                bottom: AVATAR_SIZE / 6,
              }}
            >
              <PCImage
                aspectRatio={1}
                cachePolicy="memory-disk"
                height={AVATAR_SIZE}
                recyclingKey={`${lender.profile.picture}-recent-loan`}
                source={{
                  uri: getImageVariantUrl(lender.profile.picture, 'mini'),
                }}
                style={{
                  borderRadius: AVATAR_SIZE / 2,
                  borderWidth: AVATAR_BORDER,
                  borderColor: 'white',
                }}
              />
            </View>
          ) : null}
          {borrower.profile?.picture ? (
            <View
              style={{
                marginLeft: -(AVATAR_SIZE * 0.25),
                zIndex: 0,
                top: AVATAR_SIZE / 6,
              }}
            >
              <PCImage
                aspectRatio={1}
                cachePolicy="memory-disk"
                height={AVATAR_SIZE}
                recyclingKey={`${borrower.profile.picture}-recent-loan`}
                source={{
                  uri: getImageVariantUrl(borrower.profile.picture, 'mini'),
                }}
                style={{
                  borderRadius: AVATAR_SIZE / 2,
                  borderWidth: AVATAR_BORDER,
                  borderColor: 'white',
                }}
              />
            </View>
          ) : null}
        </Row>
      </Pressable>

      {/* Text below */}
      <Column alignItems="center" style={{ paddingTop: AVATAR_SIZE / 2 + theme.spacing['spacing.general.x1'] }}>
        <Text fontSize={14} textAlign="center" variant="bodyBase" numberOfLines={3}>
          <Text fontWeight="700" fontSize={14}>
            {lenderName}
          </Text>{' '}
          {t('recent-loans.and', { defaultValue: 'et' })}{' '}
          <Text fontWeight="700" fontSize={14}>
            {borrowerName}
          </Text>{' '}
          {variant === 'exchange'
            ? t(`recent-loans.concluded.${exchangeMode}`, { defaultValue: 'ont conclu un prêt pour' })
            : t('recent-loans.shared', { defaultValue: 'ont partagé' })}{' '}
          <Text fontWeight="700" fontSize={14}>
            {itemName}
          </Text>
        </Text>
        <Text color="text.secondary" fontSize={14} textAlign="center" variant="bodyBase">
          {t('recent-loans.timeAgo', { defaultValue: 'il y a {{time}}', time: timeAgo })}
        </Text>
      </Column>
    </Column>
  );
}
